import { forwardRef, useMemo, useRef, useState } from 'react';
import { Group, Layers, Plane, Vector2, Vector2Tuple, Vector3 } from 'three';

import { useCursor } from '@react-three/drei';
import { GroupProps, MeshProps, ThreeEvent, useThree } from '@react-three/fiber';
import { DOLLHOUSE_LAYER_INTERACTIVE } from '@spazious/dollhouse-three';
import { useEventCallback } from '@spazious/react-hooks';
import { useDrag } from '@use-gesture/react';

import { Polygon } from '../Polygon/index.js';

export interface PolygonControlHandleProps extends MeshProps {
  onDrag?: (payload: { position: Vector2; first: boolean; last: boolean }) => void;
  onRemove?: () => void;
  color?: string;
  size?: number;
  hoverCursor?: string;
}

export const PolygonControlHandle: React.FC<PolygonControlHandleProps> = ({
  onClick,
  onDrag,
  onRemove,
  color = 'white',
  hoverCursor = 'move',
  size = 0.1,
  ...restProps
}) => {
  const [hover, setHover] = useState(false);
  const { controls, camera, pointer, raycaster } = useThree();

  // handles sit directly on top of the interactive polygon fill (which
  // carries the arrangement node so the whole-block DragControls can start a
  // translate from anywhere inside the outline). DragControls raycasts its
  // own fixed candidate list on a raw pointerdown listener, bypassing R3F's
  // occlusion-aware event system entirely, so it "sees through" a handle
  // that isn't in that list and grabs the fill mesh underneath instead —
  // moving the whole arrangement rather than resizing it. Registering the
  // handle on the same interactive layer, with no node in its userData,
  // makes it win that raycast (its sphere is geometrically closer to the
  // camera than the flat fill plane at the same spot) while still
  // resolving to no draggable node, so DragControls no-ops instead of
  // starting a translate.
  const layers = useMemo(() => {
    const layers = new Layers();
    layers.enable(DOLLHOUSE_LAYER_INTERACTIVE);
    return layers;
  }, []);

  const bind = useDrag(({ event, down, first, last }) => {
    event.stopPropagation();
    const plane = new Plane(new Vector3(0, 1, 0), 0);
    const pos = new Vector3();
    raycaster.setFromCamera(pointer, camera);
    raycaster.ray.intersectPlane(plane, pos);

    if (controls) {
      (controls as any).enabled = !down;
    }

    onDrag?.({ position: new Vector2(pos.x, pos.z), first: first, last: last });
  });

  useCursor(hover, hoverCursor);

  const handlePointerOver = useEventCallback((evt: ThreeEvent<PointerEvent>) => {
    evt.stopPropagation();
    setHover(true);
  });

  const handlePointerOut = useEventCallback((evt: ThreeEvent<PointerEvent>) => {
    evt.stopPropagation();
    setHover(false);
  });

  const handleClick = useEventCallback((evt: ThreeEvent<MouseEvent>) => {
    evt.stopPropagation();
    onClick?.(evt);
  });

  return (
    <mesh
      // react-gesture is not infering the EventTarget due always returns ReactDOMAttributes
      {...(bind() as any)}
      {...restProps}
      layers={layers}
      userData={{ controls: true }}
      onClick={handleClick}
      onDoubleClick={onRemove}
      onPointerOut={handlePointerOut}
      onPointerOver={handlePointerOver}
    >
      <sphereGeometry args={[size, 16, 16]} />
      <meshBasicMaterial color={color} depthTest={false} />
    </mesh>
  );
};

export interface PolygonControlsProps extends GroupProps, Pick<MeshProps, 'onClick'> {
  points: Vector2Tuple[];
  onChange?: (evt: {
    type: 'remove' | 'add' | 'update';
    first: boolean;
    last: boolean;
    points: Vector2Tuple[];
  }) => void;
  disabled?: boolean;
}

export const PolygonControls: React.FC<PolygonControlsProps> = forwardRef<
  Group,
  PolygonControlsProps
>(({ points, onChange, onClick, disabled, userData, ...restProps }, fowardedRef) => {
  const newPointsLength = useRef(-1);

  const layers = useMemo(() => {
    const layers = new Layers();
    layers.enable(DOLLHOUSE_LAYER_INTERACTIVE);
    return layers;
  }, []);

  const pointHandles = useMemo(
    () =>
      points.map((point, idx) => (
        <PolygonControlHandle
          key={`${idx}`}
          position={[point[0], 0, point[1]]}
          onClick={onClick}
          onDrag={({ position, last, first }) => {
            const newPoints = [...points];
            newPoints[idx] = [position.x, position.y] as Vector2Tuple;
            onChange?.({ type: 'update', last, first, points: newPoints });
          }}
          onRemove={() => {
            const newPoints = [...points.slice(0, idx), ...points.slice(idx + 1)];
            onChange?.({ type: 'remove', last: true, first: true, points: newPoints });
          }}
        />
      )),
    [onChange, onClick, points]
  );

  const closedLoop = useMemo(() => [...points, points[0]], [points]);

  // add create handles beetwen points
  const createHandles = useMemo(() => {
    const handles = [];
    for (let i = 0; i < points.length; i++) {
      const p1 = points[i];
      const p2 = points[i + 1] ?? points[0];
      const mid = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
      handles.push(
        <PolygonControlHandle
          key={`create-${i}`}
          hoverCursor="crosshair"
          position={[mid[0], 0, mid[1]]}
          size={0.05}
          onClick={onClick}
          onDrag={({ position, first, last }) => {
            if (first) {
              const newPoints = [
                ...points.slice(0, i + 1),
                [position.x, position.y] as Vector2Tuple,
                ...points.slice(i + 1),
              ];
              newPointsLength.current = newPoints.length;

              onChange?.({ type: 'add', last, first, points: newPoints });
            } else {
              if (last) {
                newPointsLength.current = -1;
              } else if (newPointsLength.current !== points.length) {
                // new updates not rendered yet
                return;
              }

              const newPoints = [...points];
              newPoints[i + 1] = [position.x, position.y] as Vector2Tuple;
              onChange?.({ type: 'update', last, first, points: newPoints });
            }
          }}
        />
      );
    }
    return handles;
  }, [onChange, onClick, points]);

  return (
    <group ref={fowardedRef} {...restProps}>
      <Polygon
        color="orange"
        layers={layers}
        lineWidth={2}
        points={closedLoop}
        userData={userData}
      />
      {!disabled && (
        <group renderOrder={1000}>
          {pointHandles}
          {createHandles}
        </group>
      )}
    </group>
  );
});
